import { defineComponent as _defineComponent } from 'vue'
import FormLayout from '@/components/common/FormLayout.vue'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { computed, ref } from 'vue'
import VsButton from '@/components/vision/VsButton.vue'
import JobInventoryGroupEdit from '@/components/tickets/JobInventoryGroupEdit.vue'
import { PositionedInventory } from '@/models/inventory'
import { NormalizedInventory } from '@/store/modules/assets'
import { useStore } from 'vuex-composition-helpers'
import {
  createJobStop,
  updateStopReceipts,
  useGetJobStopsQuery,
} from '@/api/jobs'
import VsForm from '@/components/vision/VsForm.vue'
import CurrentUserContext from '@/models/current-user-context'
import { isEqual, orderBy } from 'lodash'
import { useQueryClient } from '@tanstack/vue-query'
import { useCurrentOrg } from '@/common/useCurrentOrg'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import { InventoryReceipt, JobStop, JobStopType } from '@ironsight/types'

interface Props {
  jobId: string
  locationId: number
  requestedInventory: PositionedInventory[]
  activityId?: number | null
}

export default /*#__PURE__*/_defineComponent({
  __name: 'JobReceiptEdit',
  props: {
    jobId: null,
    locationId: null,
    requestedInventory: null,
    activityId: null
  },
  emits: ['job-inventory-receipts-updated'],
  setup(__props: any, { emit }) {

const props = __props as {
  jobId: string
  locationId: number
  requestedInventory: PositionedInventory[]
  activityId?: number | null
}




const receiptEditForm = ref<typeof VsForm>()
const saving = ref<boolean>(false)
const isSaveError = ref<boolean>(false)

const queryClient = useQueryClient()
const store = useStore()
const { currentOrg } = useCurrentOrg()

const { data: stopsData, isLoading, isError } = useGetJobStopsQuery(props.jobId)

const getCurrentUserContext = computed<CurrentUserContext>(
  () => store.getters['permissions/getContext']
)

const inventoriesRef = ref<PositionedInventory[]>([...props.requestedInventory])
const locationRef = ref<number>(props.locationId)

const stop = computed(() => {
  const matchingStops = stopsData.value?.filter(
    (s) => s.locationId === props.locationId
  )

  // for now we just make a stop if it's missing
  if (!matchingStops || matchingStops.length === 0) {
    return new JobStop({
      id: null,
      inventory_receipts: [],
      location_id: props.locationId,
      stop_type: JobStopType.DropOff,
    })
  } else {
    if (matchingStops[0].inventoryReceipts.length > 0) {
      inventoriesRef.value = receiptsToInventory(
        matchingStops[0].inventoryReceipts
      )
    }
    return matchingStops[0]
  }
})

const hasChanges = computed<boolean>(
  () =>
    !areReceiptsEqual(
      stop.value.inventoryReceipts.filter(
        (r) => r.ownerOrgId === currentOrg.value?.id
      )
    )
)
function areReceiptsEqual(toCompare: InventoryReceipt[]) {
  const sortFields = ['inventorySubTypeId', 'quantity']
  const incomingReceipts = orderBy(toCompare, sortFields)
  const currentReceipts = orderBy(computedReceipts.value, sortFields)
  return isEqual(incomingReceipts, currentReceipts)
}

const title = computed<string>(
  () =>
    'Confirm ' +
    (stop.value.stopType === JobStopType.PickUp ? 'Pickup' : 'Dropoff')
)
const acknowledgment = computed<string>(() => {
  const currentUserContext = getCurrentUserContext.value
  const start =
    `I` + (currentUserContext?.name ? `, ${currentUserContext?.name},` : '')

  if (stop.value.stopType === JobStopType.DropOff) {
    return `${start} acknowledge receipt of the following materials at the following location. I certify that I am authorized to confirm receipt and that the details below are correct to the best of my knowledge.`
  } else if (stop.value.stopType === JobStopType.PickUp) {
    return `${start} acknowledge delivery of the following materials at the following location. I certify that I am authorized to confirm delivery and that the details below are correct to the best of my knowledge.`
  } else {
    return ''
  }
})

const availableInventoriesByResourceTypeActivity = computed<
  (resourceTypeActivityId?: number) => NormalizedInventory[]
>(() => store.getters['assets/getInventoriesByResourceTypeActivity'])
const availableInventories = computed(() =>
  availableInventoriesByResourceTypeActivity.value(
    props.activityId ?? undefined
  )
)
const keyedInventories = computed(() =>
  inventoriesRef.value.map((item, index) => ({
    ...item,
    InvKey: `${item.inventorySubTypeId}_${index}`,
  }))
)
const computedReceipts = computed<InventoryReceipt[]>(() =>
  inventoriesRef.value.map((i) => {
    const orgId = currentOrg.value?.id
    return new InventoryReceipt({
      quantity: i.quantity ?? 0,
      inventory_sub_type_id: i.inventorySubTypeId ?? 0,
      inventory_sub_type_name: i.inventorySubTypeName ?? '',
      owner_org_id: orgId ?? '',
    })
  })
)

function receiptsToInventory(
  receipts: InventoryReceipt[]
): PositionedInventory[] {
  const orgId = currentOrg.value?.id
  return receipts
    .filter((r) => r.ownerOrgId === orgId)
    .map((r) => {
      const inventory = new PositionedInventory()
      inventory.inventorySubTypeName = r.inventorySubTypeName
      inventory.inventorySubTypeId = r.inventorySubTypeId
      inventory.quantity = r.quantity
      return inventory
    })
}

function addInventory() {
  const canUseSuggestedItem =
    !keyedInventories.value.length &&
    availableInventories.value.length === 1 &&
    availableInventories.value[0].assetConditions.length === 1

  const suggestedItem = canUseSuggestedItem
    ? availableInventories.value[0]
    : {
        id: null,
        name: null,
        assetConditions: null,
      }

  const newItem = new PositionedInventory()
  newItem.id = suggestedItem.id ?? null
  newItem.name = suggestedItem.name ?? null
  newItem.inventoryTypeId = suggestedItem.id ?? null
  newItem.inventorySubTypeId = suggestedItem.assetConditions?.[0].id ?? null
  newItem.inventorySubTypeName = suggestedItem.assetConditions?.[0].name ?? null
  newItem.inventoryId = suggestedItem.id ?? null
  newItem.pickupLocationId = stop.value.locationId
  newItem.position = 0

  inventoriesRef.value.push(newItem)
}

function updateInventory(
  newInventory: PositionedInventory & { InvKey: string }
) {
  const newInventories = [...keyedInventories.value]
  const inventoryIndex = newInventories.findIndex(
    (i) => i.InvKey === newInventory.InvKey
  )
  newInventories.splice(inventoryIndex, 1, newInventory)
  inventoriesRef.value = newInventories
}

function removeInventory(invKey: string) {
  const inventoryIndex = keyedInventories.value.findIndex(
    (i) => i.InvKey === invKey
  )
  const newInventories = [...keyedInventories.value]
  newInventories.splice(inventoryIndex, 1)
  inventoriesRef.value = newInventories
}

function isValid() {
  // Depending on your IDE settings you may see warning here for calling `isValid`.
  // It is a false positive as the VsForm is not composition API yet
  return receiptEditForm.value?.isValid()
}

async function save() {
  if (!isValid()) return

  // create a stop before we create a receipt since we don't have the driver flow yet. In the future the stop should
  // already exist before a receipt is created.
  saving.value = true

  try {
    const stopId =
      stop.value.id ?? (await createJobStop(props.jobId, stop.value))

    await updateStopReceipts(props.jobId, stopId, computedReceipts.value)

    await Promise.all([
      queryClient.invalidateQueries(['stops', 'byJobId', props.jobId]),
      queryClient.invalidateQueries(['stops', 'byId', stopId]),
    ])
  } catch (e) {
    saving.value = false
    isSaveError.value = true
    throw e
  }

  saving.value = false
  emit('job-inventory-receipts-updated')
  dismiss()
}

function tryDismiss() {
  let shouldDismiss = true
  if (hasChanges.value) {
    shouldDismiss = window.confirm(
      'This confirmation has not been submitted, are you sure you want to exit?'
    )
  }

  if (shouldDismiss) {
    dismiss()
  }
}

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const dismiss = () => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isAddReceiptBottomSheetVisible: false,
  })
}

return { __sfc: true,props, emit, receiptEditForm, saving, isSaveError, queryClient, store, currentOrg, stopsData, isLoading, isError, getCurrentUserContext, inventoriesRef, locationRef, stop, hasChanges, areReceiptsEqual, title, acknowledgment, availableInventoriesByResourceTypeActivity, availableInventories, keyedInventories, computedReceipts, receiptsToInventory, addInventory, updateInventory, removeInventory, isValid, save, tryDismiss, jobBottomSheetStatesStore, dismiss, FormLayout, VsButton, JobInventoryGroupEdit, VsForm, VsWrapper, VsSnackbar, JobStopType }
}

})